import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import ImageUpload from "@/pages/admin/components/ImageUpload";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const textFields: { key: string; label: string; type?: string }[] = [
  { key: "association_name", label: "Nom de l'association" },
  { key: "tagline", label: "Slogan" },
  { key: "phone", label: "Téléphone" },
  { key: "email", label: "Email de contact", type: "email" },
  { key: "address", label: "Adresse" },
];

const socialFields: { key: string; label: string }[] = [
  { key: "instagram", label: "Instagram" },
  { key: "snapchat", label: "Snapchat" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "youtube", label: "YouTube" },
  { key: "telegram", label: "Telegram" },
];

export default function AdminParametres() {
  const { settings, loading, error } = useSiteSettings();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (settings) {
      setForm({
        association_name: settings.association_name,
        tagline: settings.tagline,
        description: settings.description,
        phone: settings.phone,
        email: settings.email,
        address: settings.address,
        logo: settings.logo ?? "",
        logo_height: String(settings.logo_height ?? 40),
        instagram: settings.instagram,
        snapchat: settings.snapchat,
        whatsapp: settings.whatsapp,
        youtube: settings.youtube,
        telegram: settings.telegram,
      });
    }
  }, [settings]);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg("");
    setSaveError("");
    const parsedHeight = Math.max(
      16,
      Math.min(120, Number(form.logo_height) || 40)
    );
    const { error: err } = await supabase
      .from("site_settings")
      .update({
        ...form,
        logo_height: parsedHeight,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1);
    setSaving(false);
    if (err) {
      setSaveError(err.message);
      return;
    }
    setSaveMsg("Modifications enregistrées avec succès.");
  }

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Infos générales
      </h1>
      <p className="mt-2 text-sm md:text-base text-foreground-600">
        Ces informations sont utilisées sur le site (contact, réseaux sociaux,
        pied de page).
      </p>

      {error && (
        <div className="mt-6 rounded-md bg-primary-100 text-primary-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-8 bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 space-y-5"
      >
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            Logo de l'association
          </label>
          <ImageUpload
            value={form.logo ?? ""}
            onChange={(url) => update("logo", url)}
            placeholder="Logo affiché dans l'en-tête et le pied de page"
          />
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-foreground-700 whitespace-nowrap">
              Hauteur du logo
            </label>
            <input
              type="number"
              min="16"
              max="120"
              step="1"
              value={form.logo_height ?? ""}
              onChange={(e) => update("logo_height", e.target.value)}
              className="w-24 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
            <span className="text-sm text-foreground-500">px (16 à 120)</span>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500 mr-1">Test rapide :</span>
            {[32, 50, 64].map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => update("logo_height", String(h))}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition cursor-pointer ${
                  Number(form.logo_height) === h
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {h} px
              </button>
            ))}
          </div>

          {form.logo && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-200 p-4">
                <p className="text-xs text-foreground-500 mb-3">Aperçu en-tête (fond clair)</p>
                <div className="flex items-center h-20 justify-center bg-background-50">
                  <img
                    src={form.logo}
                    alt="Aperçu du logo"
                    style={{ height: `${Math.max(16, Math.min(120, Number(form.logo_height) || 40))}px` }}
                    className="w-auto object-contain"
                  />
                </div>
              </div>
              <div className="rounded-lg border border-background-200 overflow-hidden">
                <p className="text-xs text-foreground-500 mb-3">Aperçu pied de page (fond sombre)</p>
                <div className="flex items-center h-20 justify-center bg-secondary-900">
                  <img
                    src={form.logo}
                    alt="Aperçu du logo"
                    style={{ height: `${Math.max(16, Math.min(120, Number(form.logo_height) || 40))}px` }}
                    className="w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {textFields.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              {field.label}
            </label>
            <input
              type={field.type ?? "text"}
              value={form[field.key] ?? ""}
              onChange={(e) => update(field.key, e.target.value)}
              className={inputClass}
            />
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            Description (présentation)
          </label>
          <textarea
            rows={3}
            value={form.description ?? ""}
            onChange={(e) => update("description", e.target.value)}
            className={inputClass}
          />
        </div>

        <div className="pt-4 border-t border-background-200">
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            Réseaux sociaux
          </h3>
          <p className="mt-1 text-sm text-foreground-500">
            Laissez un champ vide pour masquer le réseau correspondant.
          </p>
          <div className="mt-4 space-y-4">
            {socialFields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {field.label}
                </label>
                <input
                  type="text"
                  value={form[field.key] ?? ""}
                  onChange={(e) => update(field.key, e.target.value)}
                  placeholder="https://…"
                  className={inputClass}
                />
              </div>
            ))}
          </div>
        </div>

        {saveMsg && (
          <div className="rounded-md bg-primary-100 text-primary-700 px-4 py-3 text-sm">
            <i className="ri-checkbox-circle-line mr-1.5"></i>
            {saveMsg}
          </div>
        )}
        {saveError && (
          <div className="rounded-md bg-accent-100 text-accent-700 px-4 py-3 text-sm">
            <i className="ri-error-warning-line mr-1.5"></i>
            {saveError}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-8 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Enregistrement…
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Enregistrer
            </>
          )}
        </button>
      </form>
    </div>
  );
}